import { FormBuilder, FormControl } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { map, startWith } from 'rxjs/operators';
import { ILabels } from "libraries/interfaces/ILabels";
import { Yii } from "libraries/interfaces/Yii";
import {
    IQuotationsSuppliersCreateInput,
    IQuotationsSuppliersSearch,
} from 'libraries/service/resources/quotation-quotations/quotation-quotations-resource.service';
import { IFieldSpecs } from 'libraries/module/formbuilder/fields/interfaces/field-specs';
import { ICellOutput, QuotationCellsActions } from 'libraries/service/resources/quotation-quotations/IQuotationsQuotation';
import { AutocompleteComponent } from 'libraries/module/formbuilder/fields/autocomplete/autocomplete.component';
import { ErrorTracingService } from 'libraries/service/error-tracing/error-tracing.service';
import { PermissionResourceService } from 'libraries/service/resources/permission/permissionResource.service';
import { ITableBlock } from 'app_quotation/shared/services/table-data/ITable';
import { ICoordsTable } from "libraries/service/table-keyboard-navigation/table-keyboard-navigation.service";
import { ICurrencyOption } from "libraries/service/resources/currency-list/currency-list-resource.service";

@Component({
    selector: 'block-supplier',
    templateUrl: 'block-supplier.component.html',
    styleUrls: ["block-supplier.component.css"],
})

export class BlockSupplierComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
    @Input()
    public colspan: number = 1;
    @Input()
    public block: ITableBlock = null;
    @Input()
    public suppliers: IQuotationsSuppliersSearch[] = [];
    @Input()
    public isEditable: boolean = true;
    @Input()
    public defaultCurrencies: ICurrencyOption[];
    @Input()
    public quotationCurrency: string;
    @Input()
    public isEmpty: boolean;
    /**
     * Indicate that Edition is opened from keyboard navigation  in parent component
     */
    @Input()
    public isKeyboardOpen: boolean;


    @Output()
    public readonly onCostChange: EventEmitter<ICellOutput> = new EventEmitter<ICellOutput>();
    @Output()
    public readonly onFocusField: EventEmitter<ICoordsTable> = new EventEmitter<ICoordsTable>();
    @Output()
    public readonly onOpenPanel: EventEmitter<void> = new EventEmitter<void>();
    @Output()
    public readonly onClosePanel: EventEmitter<void> = new EventEmitter<void>();
    @Output()
    public readonly onInputChange: EventEmitter<string> = new EventEmitter<string>();

    public wording: ILabels = {
        supplier: Yii.t('wui-quotation-js', 'Fournisseur')
    };

    public editSupplier: boolean = false;
    public isBlurOnInput: boolean = false;
    public fieldConfig: IFieldSpecs;
    public filteredOptions: Observable<IQuotationsSuppliersSearch[]>;
    public input: BehaviorSubject<string> = new BehaviorSubject('');
    public selectedOption: string = '';
    public originalSupplier: string = '';
    public autocompleteDisabled: boolean = true;
    public hasPermissionToCreate: boolean = false;

    @ViewChild('focuser')
    public focuser: ElementRef;

    @ViewChild('autocomplete')
    public autocomplete: AutocompleteComponent<IQuotationsSuppliersSearch[]>;

    @ViewChild('template', {read: TemplateRef, static: true})
    private readonly template: TemplateRef<ElementRef>;

    private supplierField: FormControl;
    private editionStateHandle: NodeJS.Timer;
    private isBlurOnInputHandle: NodeJS.Timer;
    private disableOpenOnEnter: boolean = false;
    private focusAskedBeforeInit: boolean = false;

    constructor(
        private readonly viewContainerRef: ViewContainerRef,
        private readonly formBuilder: FormBuilder,
        private readonly permissionResource: PermissionResourceService,
        private readonly errorTracing: ErrorTracingService
    ) {
    }

    public ngOnInit(): void {
        this.supplierField = this.formBuilder.control(this.block.supplier);

        this.supplierField.valueChanges
            .subscribe({
                next: (value: IQuotationsSuppliersSearch | IQuotationsSuppliersCreateInput): void => {
                    this.changeInput(value && value.name, true);
                }
            });

        this.fieldConfig = {
            formGroup: this.formBuilder.group({
                supplier: this.supplierField,
            }),
            formControlName: 'supplier',
            className: 'invisible-input'
        };

        if (this.block.supplier) {
            this.selectedOption = this.block.supplier.name;
        }

        this.viewContainerRef.createEmbeddedView(this.template);
        this.getSupplierOptionsByCategory(this.block.category);

        this.permissionResource.hasPermission("quotation.supplier.create")
            .subscribe({
                next: (hasPermissionToCreate: boolean): void => {
                    this.hasPermissionToCreate = hasPermissionToCreate;
                },
                error: this.errorTracing.captureException.bind(this.errorTracing),
            });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.block && changes.block.currentValue !== changes.block.previousValue && this.fieldConfig) {
            this.fieldConfig.formGroup.patchValue({
                supplier: this.block && this.block.supplier
            });
            this.selectedOption = this.block.supplier && this.block.supplier.name || undefined;
            this.input.next(this.block && this.block.supplier && this.block.supplier.name || "");
        }

        if(changes.isKeyboardOpen && changes.isKeyboardOpen.currentValue && this.focuser && this.focuser.nativeElement){
            this.focuser.nativeElement.focus();
        }

        if (changes.suppliers) {
            this.input.next(this.selectedOption || "");
        }
    }

    public ngAfterViewInit(): void {
        if (this.focusAskedBeforeInit) {
            this.focus();
        }
    }

    public ngOnDestroy(): void {
        this.input.complete();
    }

    public getSupplierOptionsByCategory(category: string): void {
        this.filteredOptions = this.input.asObservable()
            .pipe(
                startWith(this.block && this.block.supplier && this.block.supplier.name || ""),
                map((value: string) => filterOptions(
                    this.suppliers.filter((supplierOption: IQuotationsSuppliersSearch) => supplierOption.categories.includes(category)),
                    value
                ))
            );
    }

    public changeInput(input: string, changeAfterValueSelection?: boolean): void {
        if (!changeAfterValueSelection) {
            this.autocompleteDisabled = false;
        }

        this.input.next(input);
        this.onInputChange.emit(input);
        this.selectedOption = input;
    }

    public displaySupplier(supplier: IQuotationsSuppliersSearch): string {
        return supplier && supplier.name;
    }

    public setEdit(): void {
        if (this.isEditable) {
            this.originalSupplier = this.selectedOption;
            this.onFocusField.emit(null);
            this.changeIsBlurOnInput(false);
            this.changeEditionState(true);
        }
    }

    public valueSelected(): void {
        this.disableOpenOnEnter = true;
        this.unsetEdit();
        this.autocomplete.focus();
        this.changeEditionState(true);
    }

    public unsetEdit(): void {
        this.changeIsBlurOnInput(false);
        this.changeEditionState(false);

        if (!this.isEditable) {
            return;
        }

        const supplier: IQuotationsSuppliersCreateInput | IQuotationsSuppliersSearch = this.supplierField.value;
        const existingSupplier: IQuotationsSuppliersSearch = this.suppliers
            .find((candidate: IQuotationsSuppliersSearch) => supplier
                && supplier.name
                && supplier.name.trim().toLowerCase() === candidate.name.trim().toLowerCase());

        this.selectedOption = supplier && supplier.name;

        if (!supplier) {
            this.selectedOption = undefined;

            if (this.block.supplier) {
                this.onCostChange.emit({
                    id: this.block.id,
                    itemType: QuotationCellsActions.updateSupplier,
                    supplierId: null
                });
            }

            return;
        }

        this.selectedOption = supplier.name;

        if (existingSupplier && existingSupplier.id) {

            if (!this.block.supplier || this.block.supplier.id !== existingSupplier.id) {
                this.onCostChange.emit({
                    id: this.block.id,
                    itemType: QuotationCellsActions.updateSupplier,
                    cost: {
                        currency: existingSupplier.preferredCurrency
                    },
                    supplierId: existingSupplier.id
                });
            }

            return;
        }

        this.onCostChange.emit({
            itemType: QuotationCellsActions.createSupplier,
            id: this.block.id,
            ...supplier
        });
    }


    public onClickEvent(): void {
        if (this.isEditable && this.focuser) {
            this.autocompleteDisabled = false;
            this.focuser.nativeElement.focus();
        }
    }

    public isExactMatch(suppliers: IQuotationsSuppliersSearch, input: string): boolean {
        return suppliers && input && suppliers.name.toLowerCase() === input.toLowerCase();
    }

    public createSupplier(text: string): IQuotationsSuppliersCreateInput {
        return {
            name: text,
            preferredCurrency: this.quotationCurrency,
            categories: [this.block.category],
            currencies: this.defaultCurrencies.map((currency: ICurrencyOption) => currency.value)
        };
    }

    public closePanel(): void {
        this.autocompleteDisabled = true;
        this.disableOpenOnEnter = false;
        this.onClosePanel.emit();
    }

    public openPanel(): void {
        this.onOpenPanel.emit();
        this.disableOpenOnEnter = true;
    }

    public onKeyUp(event: KeyboardEvent): void {
        if (event.key === "Enter" && !this.disableOpenOnEnter) {
            this.autocompleteDisabled = false;
            this.autocomplete.openPanel();
            event.stopPropagation();
        }
    }

    public focus(): void {
        if (!this.focuser || !this.focuser.nativeElement) {
            this.focusAskedBeforeInit = true;
            return;
        }

        if (this.focuser && this.isEditable) {
            this.changeIsBlurOnInput(false);
            this.focuser.nativeElement.focus();
            this.disableOpenOnEnter = false;
            this.changeEditionState(true);
        }

        this.focusAskedBeforeInit = false;
    }

    public hasFocus(): boolean {
        return this.focuser && this.focuser.nativeElement && this.focuser.nativeElement === document.activeElement;
    }

    public blur(): void {
        if (this.focuser && this.focuser.nativeElement) {
            this.changeIsBlurOnInput(true);
            this.focuser.nativeElement.blur();
            this.changeEditionState(false);
        }

        this.focusAskedBeforeInit = false;
    }

    private changeEditionState(value: boolean): void {
        // This setTimeout is used to avoid angular errors
        // saying that the value changed after check

        if (this.editionStateHandle) {
            clearTimeout(this.editionStateHandle);
        }

        this.editionStateHandle = setTimeout(() => {
            this.editSupplier = value;
        }, 1);
    }

    private changeIsBlurOnInput(newValue: boolean): void {
        if (this.isBlurOnInputHandle) {
            clearTimeout(this.isBlurOnInputHandle);
        }

        this.isBlurOnInputHandle = setTimeout(() => {
            this.isBlurOnInput = newValue;
        }, 1);
    }
}

function filterOptions(
    options: IQuotationsSuppliersSearch[],
    value: string | IQuotationsSuppliersSearch | IQuotationsSuppliersCreateInput
): IQuotationsSuppliersSearch[] {
    const searchValue: string = !value
        ? ""
        : typeof value === "string"
            ? value
            : value.name;

    const filterValue: string = searchValue.trim().toLowerCase();
    return options.filter((option: IQuotationsSuppliersSearch) => option.name.toLowerCase().includes(filterValue));
}
